/**
 * Extracts property names from a destructured object parameter
 * @param {string} objectStr - The object parameter string
 * @returns {Array} - Array of property names
 */
const extractPropertiesFromObject = objectStr => {
    // Match the content inside the braces
    const match = objectStr.match(/\{\s*([^}]*)\s*\}/);
    if (!match) return [];

    const propertiesStr = match[1];

    // Split by comma and process each property
    return propertiesStr
        .split(',')
        .map(prop => prop.trim())
        .filter(prop => prop && !prop.startsWith('...'))
        .map(prop => prop.split(':')[0].trim())
        .filter(Boolean);
};

/**
 * Extracts the first parameter from a parameter string
 * @param {string} paramStr - The full parameter string
 * @returns {string} - The first parameter
 */
const extractFirstParameter = paramStr => {
    let braceCount = 0;
    let endIndex = paramStr.length;

    // Find where the first parameter ends
    for (let i = 0; i < paramStr.length; i++) {
        const char = paramStr[i];

        if (char === '{') braceCount++;
        else if (char === '}') braceCount--;
        else if (char === ',' && braceCount === 0) {
            endIndex = i;
            break;
        }
    }

    return paramStr.substring(0, endIndex).trim();
};

/**
 * Extracts parameter names from the first argument of a function
 * @param {Function} func - The function to extract parameters from
 * @returns {Array} - Array of parameter names
 */
export const extractParamsFromFunction = func => {
    // Guard against invalid input
    if (!func || typeof func !== 'function') {
        return [];
    }

    const funcStr = func.toString();

    // Extract the entire parameter string
    const paramMatch = funcStr.match(/\(([^)]*)\)/);
    if (!paramMatch || !paramMatch[1].trim()) {
        return [];
    }

    const fullParamStr = paramMatch[1];

    // Extract the first parameter (handle nested braces correctly)
    const firstParam = extractFirstParameter(fullParamStr);
    if (!firstParam || firstParam.startsWith('...')) {
        return [];
    }

    // If it's a destructured object, extract its properties
    if (firstParam.startsWith('{')) {
        return extractPropertiesFromObject(firstParam);
    }

    // Handle simple parameter (remove default value if present)
    return [firstParam.split('=')[0].trim()];
};
